import { Address, decodeEventLog, encodeEventTopics, maxUint256 } from "viem";
import {
  HealthFactorVariations,
  calculateHealthFactor,
} from "../../helpers/math/calculateHealthFactor";
import determineLoanStatus from "../../helpers/loans/determineLoanStatus";
import {
  actionContract,
  auctionContract,
  marketContract,
  sellNowContract,
  uTokenFactoryContract,
} from "../../../contracts";
import {
  NFTWithValuation,
  calculateMinimumRepay,
} from "@unlockdfinance/unlockd-math";
import { MarketItemType } from "../../../contracts/MarketContract";
import UnlockdService from "../../../data/UnlockdService";
import { OptionsWriteMethod, Output } from "@unlockdfinance/verislabs-web3";
import {
  areTheSameNft,
  equalIgnoreCase,
} from "@unlockdfinance/verislabs-web3/utils";
import { app } from "config/app.config";
import { INft, SimpleNft } from "../nft/INft";
import {
  calculateAvailableToBorrow,
  calculateLiquidationPrice,
  calculateLoanValuation,
  calculateLtv,
} from "logic/helpers/math";
import { LoanModel, MarketItemModel, NftModel } from "data/store/models";
import unlockdWalletModule from "logic/UnlockdWalletModule";
import { getNftId } from "logic/helpers/nfts/nftId";
import NftsModule from "logic/NftsModule";
import contractsService from "data/ContractsService";
import RedeemData from "./RedeemData";
import createAssetId from "logic/helpers/createAssetId";
import MarketItemData from "../nft/MarketItemData";
import { LoanStatus } from "./types";
import { TheGraphService } from "data/TheGraphService";
import { SupportedChainIds } from "clients/verisModule";

export type LoanOptions = Parameters<typeof LoanModel.save>[1];

export class Loan {
  static model = LoanModel;

  readonly id: Address;
  readonly nfts: INft[];
  readonly borrower: Address;
  readonly underlyingAsset: Address;

  // example: 0.85 is expressed as 8500n
  // at the beginning the liquidation threshold
  // will be 8500 for all the nfts
  readonly liquidationThreshold: bigint;

  static getLoanById(id: Address) {
    // look for the loan in the model
    // if not look for the loan in the service (the graph)
  }

  static async borrow(
    chainId: SupportedChainIds,
    underlyingAsset: Address,
    amount: bigint,
    nfts: INft[],
    options?: OptionsWriteMethod
  ) {
    options?.onServerSignPending?.();

    const { data, signature } = await UnlockdService.get(chainId).getBorrowSignature(
      underlyingAsset,
      nfts
    );

    const output = await actionContract.borrow(
      amount,
      this.parseNftsToBorrow(nfts),
      data,
      signature,
      options
    );

    nfts.forEach((nft) => {
      NftsModule.updateNftFromCache(
        unlockdWalletModule.unlockdAddress!,
        nft.collection,
        nft.tokenId,
        nft.chainId,
        { isDeposited: true }
      );
    });

    return output;
  }

  static async getCurrentBorrowAmounts(
    loans: { id: string; underlyingAsset: Address }[],
    chainId: SupportedChainIds
  ) {
    const currentBorrowAmountsPromises = loans.map(
      ({ id, underlyingAsset }) => {
        return uTokenFactoryContract.getDebtFromLoanId(
          underlyingAsset,
          id as Address,
          chainId
        );
      }
    );

    return await Promise.all(currentBorrowAmountsPromises);
  }

  private static parseNftsToBorrow(nfts: SimpleNft[]) {
    return nfts.map(({ collection, tokenId }) => ({
      collection,
      tokenId: BigInt(tokenId),
    }));
  }

  constructor({
    borrower,
    id,
    nfts,
    underlyingAsset,
    options,
  }: {
    id: Address;
    nfts: INft[];
    borrower: Address;
    underlyingAsset: Address;
    options?: LoanOptions;
  }) {
    this.id = id;
    this.nfts = nfts;
    this.borrower = borrower;
    this.underlyingAsset = underlyingAsset;

    this.liquidationThreshold = app.DEFAULT_LIQ_THRESHOLD_VALUE;

    if (options) {
      Loan.model.save(this.cacheId, options);
    }
  }

  get cacheId() {
    return `${this.currency.chain.id}-${this.id}`;
  }

  get currentBorrowAmount() {
    return new Promise<bigint>(async (resolve) => {
      const { currentBorrowAmount: amountFromCache } =
        Loan.model.findByIdLeanNullSafe(this.cacheId);

      if (amountFromCache) {
        resolve(amountFromCache as bigint);
      }

      let amountFromChain: bigint;

      if (await this.isAuctioned) {
        const { bidderBonus, totalAmount } = await this.redeemData;

        amountFromChain = totalAmount - bidderBonus;
      } else {
        amountFromChain = await contractsService.getCurrentBorrowFromLoan(
          this.id,
          this.underlyingAsset,
          this.currency.chain.id
        );
      }

      Loan.model.save(this.cacheId, { currentBorrowAmount: amountFromChain });

      resolve(amountFromChain as bigint);
    });
  }

  // PRICES
  get valuation() {
    return new Promise<bigint>(async (resolve) => {
      const prices = await Promise.all(this.nfts.map((nft) => nft.prices));

      resolve(calculateLoanValuation(prices));
    });
  }

  get ltv() {
    return new Promise<bigint>(async (resolve) => {
      const prices = await Promise.all(
        this.nfts.map((nft) => ({ ...nft.prices }))
      );

      resolve(calculateLtv(prices));
    });
  }

  get liquidationPrice() {
    return new Promise<bigint>(async (resolve, reject) => {
      try {
        const [currentBorrowAmount, liquidationThreshold] = await Promise.all([
          this.currentBorrowAmount,
          this.liquidationThreshold,
        ]);

        resolve(
          calculateLiquidationPrice(currentBorrowAmount, liquidationThreshold)
        );
      } catch (err) {
        reject(err);
      }
    });
  }

  get availableToBorrow() {
    return new Promise<bigint>(async (resolve) => {
      const [nfts, currentBorrowAmount] = await Promise.all([
        Promise.all(
          this.nfts.map((nft) => {
            return new Promise<{
              availableToBorrow: bigint;
              valuation: bigint;
              ltv: bigint;
            }>(async (resolve) => {
              const [availableToBorrow, valuation, ltv] = await Promise.all([
                nft.availableToBorrow,
                nft.valuation,
                nft.ltv,
              ]);

              resolve({ availableToBorrow, valuation, ltv });
            });
          })
        ),
        this.currentBorrowAmount,
      ]);

      resolve(calculateAvailableToBorrow(nfts, currentBorrowAmount));
    });
  }

  get healthFactor() {
    return new Promise<bigint>(async (resolve) => {
      const [valuation, currentBorrowAmount, liquidationThreshold] =
        await Promise.all([
          this.valuation,
          this.currentBorrowAmount,
          this.liquidationThreshold,
        ]);

      resolve(
        calculateHealthFactor(
          valuation,
          currentBorrowAmount,
          liquidationThreshold
        )
      );
    });
  }

  get status(): Promise<LoanStatus> {
    return new Promise<LoanStatus>(async (resolve) => {
      const [
        currentBorrowAmount,
        healthFactor,
        availableToBorrow,
        isAuctioned,
      ] = await Promise.all([
        this.currentBorrowAmount,
        this.healthFactor,
        this.availableToBorrow,
        this.isAuctioned,
      ]);

      resolve(
        determineLoanStatus(
          currentBorrowAmount,
          healthFactor,
          availableToBorrow,
          isAuctioned
        )
      );
    });
  }

  get currency() {
    return this.nfts[0].currency;
  }

  get isRemoveNftsAvailable(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const [currentBorrowAmount, status] = await Promise.all([
        this.currentBorrowAmount,
        this.status,
      ]);

      resolve(
        currentBorrowAmount === BigInt(0) ||
        (this.isMulticollateral &&
          (status === LoanStatus.ONLY_REPAY ||
            status === LoanStatus.TO_BORROW))
      );
    });
  }

  isListForSaleAvailable(nft: INft): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const [isHfBelowOne, isAuctioned, isListed] = await Promise.all([
        this.isHfBelowOne,
        this.isAuctioned,
        nft.isListed,
      ]);

      resolve(!isHfBelowOne && !isAuctioned && !isListed);
    });
  }

  get isInstantSellAvailable(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      resolve(!(await this.isAuctioned));
    });
  }

  get isMulticollateral() {
    return this.nfts.length > 1;
  }

  get isHfBelowOne(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) =>
      resolve((await this.healthFactor) < 1000)
    );
  }

  get isAuctioned(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const marketItemDatas = await Promise.all(
        this.nfts.map((nft) => nft.marketItemData)
      );

      resolve(
        marketItemDatas.some(
          (data) =>
            data != undefined &&
            data.type === MarketItemType.TYPE_LIQUIDATION_AUCTION &&
            data.bids.length > 0
        )
      );
    });
  }

  get redeemEnd(): Promise<number | null> {
    return new Promise<number | null>(async (resolve) => {
      const marketItemDatas = await Promise.all(
        this.nfts.map((nft) => nft.marketItemData)
      );

      const dataIndex = marketItemDatas.findIndex(
        (data) =>
          data != undefined &&
          data.type === MarketItemType.TYPE_LIQUIDATION_AUCTION &&
          data.bids.length > 0
      );

      if (dataIndex !== -1) {
        resolve(marketItemDatas[dataIndex]!.biddingEnd);
      } else {
        resolve(null);
      }
    });
  }

  get auctionFinished(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const redeemEnd = await this.redeemEnd;

      resolve(redeemEnd !== null && redeemEnd < Date.now());
    });
  }

  get redeemData(): Promise<RedeemData> {
    return new Promise(async (resolve) => {
      const { redeemData } = Loan.model.findByIdLeanNullSafe(this.cacheId);

      if (redeemData) {
        resolve(redeemData);
      } else {
        const data = await contractsService.getRedeemData(
          this.id,
          this.assetIds,
          this.nfts[0].chainId,
          false,
        );

        Loan.model.save(this.id, { redeemData: data });

        resolve(data);
      }
    });
  }

  get bidFine(): Promise<bigint> {
    return new Promise(async (resolve) => {
      const redeemData = await this.redeemData;

      resolve(redeemData.bidderBonus);
    });
  }

  get redeemAmount(): Promise<bigint> {
    return new Promise(async (resolve) => {
      const redeemData = await this.redeemData;

      resolve(redeemData.totalAmount);
    });
  }

  get assetIds(): Address[] {
    return this.nfts.map((nft) => createAssetId(nft.collection, nft.tokenId));
  }

  async redeem(options?: OptionsWriteMethod): Promise<Output<void>> {
    options?.onServerSignPending?.();

    const { data, signature } = await UnlockdService.get(this.currency.chain.id).getRedeemSignature(
      this.id
    );

    const output = await auctionContract.redeem(data, signature, options);

    Loan.model.save(this.cacheId, {
      redeemData: undefined,
      currentBorrowAmount: BigInt(0),
    });

    this.nfts.forEach((nft) => {
      const { marketItemData } = NftModel.findByIdLeanNullSafe(nft.nftId);

      if (
        marketItemData &&
        marketItemData.type === MarketItemType.TYPE_LIQUIDATION_AUCTION
      ) {
        NftModel.save(nft.nftId, { marketItemData: undefined });
      }
    });

    return output;
  }

  apy(): Promise<number> {
    return this.currency.getBorrowApy();
  }

  async calculateHealthFactor(
    variations: HealthFactorVariations
  ): Promise<bigint> {
    const [valuation, currentBorrowAmount, liquidationThreshold] =
      await Promise.all([
        this.valuation,
        this.currentBorrowAmount,
        this.liquidationThreshold,
      ]);

    return calculateHealthFactor(
      valuation,
      currentBorrowAmount,
      liquidationThreshold,
      variations
    );
  }

  async calculateIfInstantSellIsPossible(
    nftToSell: INft,
    price: bigint
  ): Promise<boolean> {
    const amountToReceive = await this.calculateAmuntToReceiveOnInstantSell(
      nftToSell,
      price
    );

    return !(amountToReceive < BigInt(0));
  }

  async calculateAmuntToReceiveOnInstantSell(
    nftToSell: INft,
    price: bigint
  ): Promise<bigint> {
    const index = this.nfts.findIndex((nft) => areTheSameNft(nftToSell, nft));

    if (index === -1) {
      throw new Error("nft to sell is not included in this loan");
    }

    const [initialLoans, totalDebt] = await Promise.all([
      Promise.all(
        this.nfts.map(
          (nft) =>
            new Promise<NFTWithValuation>(async (resolve) => {
              const [valuation, ltv] = await Promise.all([
                nft.valuation,
                nft.ltv,
              ]);

              resolve({ ...nft, valuation, ltv });
            })
        )
      ),
      this.currentBorrowAmount,
    ]);

    const minToRepay = calculateMinimumRepay({
      initialLoans,
      indicesToDelete: [index],
      totalDebt,
    });

    return price - minToRepay;
  }

  async calculateAvailableToBorrow(variations: {
    nftsToRemove?: INft[];
    nftsToAdd?: INft[];
    debt?: bigint;
  }): Promise<bigint> {
    let [nfts, totalDebt, nftsToAdd] = await Promise.all([
      Promise.all(
        this.nfts.map(
          (nft) =>
            new Promise<{
              collection: Address;
              tokenId: string;
              availableToBorrow: bigint;
            }>(async (resolve) => {
              const availableToBorrow = await nft.availableToBorrow;

              resolve({
                ...nft,
                collection: nft.collection,
                availableToBorrow,
              });
            })
        )
      ),
      this.currentBorrowAmount,
      variations.nftsToAdd &&
      Promise.all(
        variations.nftsToAdd.map(
          (nft) =>
            new Promise<{
              collection: Address;
              tokenId: string;
              availableToBorrow: bigint;
            }>(async (resolve) => {
              const availableToBorrow = await nft.availableToBorrow;

              resolve({
                ...nft,
                collection: nft.collection,
                availableToBorrow,
              });
            })
        )
      ),
    ]);

    if (variations.debt) totalDebt += variations.debt;

    if (nftsToAdd) nfts.push(...nftsToAdd);

    const maximumAmount = nfts.reduce((acc, nft) => {
      if (variations.nftsToRemove) {
        const isBeingRemoved = variations.nftsToRemove.some(
          ({ collection, tokenId }) =>
            equalIgnoreCase(collection, nft.collection) &&
            tokenId === nft.tokenId
        );

        if (!isBeingRemoved) {
          acc += nft.availableToBorrow;
        }
      } else {
        acc += nft.availableToBorrow;
      }

      return acc;
    }, BigInt(0));

    if (totalDebt > maximumAmount) return BigInt(0);

    return maximumAmount - totalDebt;
  }

  async calculateMinToRepay(nftsToRemove: INft[]): Promise<bigint> {
    const indexes: number[] = nftsToRemove.map((nftToRemove) => {
      const index = this.nfts.findIndex((nft) =>
        areTheSameNft(nft, nftToRemove)
      );

      if (index === -1) {
        throw new Error("Nft provided is not included in the loan");
      }

      return index;
    });

    const [initialLoans, totalDebt] = await Promise.all([
      Promise.all(
        this.nfts.map(
          (nft) =>
            new Promise<NFTWithValuation>(async (resolve) => {
              const [valuation, ltv] = await Promise.all([
                nft.valuation,
                nft.ltv,
              ]);

              resolve({ ...nft, valuation, ltv });
            })
        )
      ),
      this.currentBorrowAmount,
    ]);

    return calculateMinimumRepay({
      initialLoans,
      indicesToDelete: indexes,
      totalDebt,
    });
  }

  async borrowMore(
    amount: bigint,
    newNfts?: INft[],
    options?: OptionsWriteMethod
  ): Promise<Output<void>> {
    options?.onServerSignPending?.();

    const { data, signature } = await UnlockdService.get(this.currency.chain.id).getBorrowSignature(
      undefined,
      newNfts,
      this.id
    );

    const nfts =
      newNfts?.map(({ collection, tokenId }) => ({
        collection,
        tokenId: BigInt(tokenId),
      })) || [];

    const output = await actionContract.borrow(
      amount,
      nfts,
      data,
      signature,
      options
    );

    if (newNfts && nfts.length > 0) {
      const nftIds = newNfts.map((nft) => {
        NftsModule.updateNftFromCache(
          unlockdWalletModule.unlockdAddress!,
          nft.collection,
          nft.tokenId,
          nft.chainId,
          { isDeposited: true }
        );

        return getNftId(nft.collection, nft.tokenId, nft.chainId);
      });

      const { nfts: nftsFromModel } = Loan.model.findByIdLeanNullSafe(this.cacheId);

      if (nftsFromModel) {
        nftIds.unshift(...nftsFromModel);
      }

      Loan.model.save(this.cacheId, { nfts: nftIds });
    }

    return output;
  }

  async addNfts(
    nfts: SimpleNft[],
    options?: OptionsWriteMethod
  ): Promise<Output<void>> {
    options?.onServerSignPending?.();

    const { data, signature } = await UnlockdService.get(this.currency.chain.id).getBorrowSignature(
      undefined,
      nfts,
      this.id
    );

    const output = await actionContract.borrow(
      BigInt(0),
      Loan.parseNftsToBorrow(nfts),
      data,
      signature,
      options
    );

    nfts.forEach((nft) => {
      NftsModule.updateNftFromCache(
        unlockdWalletModule.unlockdAddress!,
        nft.collection,
        nft.tokenId,
        this.currency.chain.id,
        { isDeposited: true }
      );
    });

    const newNfts = nfts.map((nft) => getNftId(nft.collection, nft.tokenId, this.currency.chain.id));

    const { nfts: nftsFromModel } = Loan.model.findByIdLeanNullSafe(this.cacheId);

    if (nftsFromModel) {
      newNfts.unshift(...nftsFromModel);
    }

    Loan.model.save(this.cacheId, { nfts: newNfts });

    return output;
  }

  async removeNftsAndRepay(
    nfts: SimpleNft[],
    amountToRepay?: bigint,
    options?: OptionsWriteMethod
  ): Promise<Output<void>> {
    options?.onServerSignPending?.();
    let correctedAmount: bigint;

    const isFullyRepay = amountToRepay === (await this.currentBorrowAmount);

    const correctedNfts = isFullyRepay ? this.nfts : nfts;

    if (amountToRepay) {
      correctedAmount = isFullyRepay ? maxUint256 : amountToRepay;
    } else {
      correctedAmount = BigInt(0);
    }

    const { data, signature } = await UnlockdService.get(this.currency.chain.id).getRepaySignature(
      this.id,
      correctedNfts
    );

    const output = await actionContract.repay(
      correctedAmount,
      data,
      signature,
      options
    );

    const nftIds = correctedNfts.map((nft) => {
      NftsModule.updateNftFromCache(
        unlockdWalletModule.unlockdAddress!,
        nft.collection,
        nft.tokenId,
        this.currency.chain.id,
        { isDeposited: false }
      );

      return getNftId(nft.collection, nft.tokenId, this.currency.chain.id);
    });

    if (isFullyRepay) {
      Loan.model.deleteById(this.cacheId);
    } else {
      const currentBorrowAmount =
        await contractsService.getCurrentBorrowFromLoan(
          this.id,
          this.underlyingAsset,
          this.currency.chain.id,
          true
        );

      const { nfts: nftsFromModel } = Loan.model.findByIdLeanNullSafe(this.cacheId);

      if (nftsFromModel) {
        const remainingNfts = nftsFromModel.filter(
          (nftId) => !nftIds.includes(nftId)
        );

        Loan.model.save(this.cacheId, { nfts: remainingNfts, currentBorrowAmount });
      }
    }

    return output;
  }

  async instantSell(
    nft: INft,
    options?: OptionsWriteMethod
  ): Promise<Output<void>> {
    const unlockdAccount = unlockdWalletModule.unlockdAddress!;

    options?.onServerSignPending?.();

    const { data, signature } = await UnlockdService.get(this.currency.chain.id).getSellNowSignature(
      this.id,
      nft
    );

    const output = await sellNowContract.sellNow(nft, data, signature, options);

    const { nfts } = Loan.model.findByIdLeanNullSafe(this.cacheId);

    const nftId = getNftId(nft.collection, nft.tokenId, nft.chainId);

    if (nfts) {
      const newNfts = nfts.filter((_nftId) => nftId !== _nftId);

      Loan.model.save(this.cacheId, { nfts: newNfts });
    }

    NftModel.save(nftId, { owner: undefined });

    NftsModule.removeNftFromCache(unlockdAccount, nft.collection, nft.tokenId, nft.chainId);

    return output;
  }

  async listForSale(
    nft: INft,
    marketType: MarketItemType,
    debtListing: bigint,
    finishDate: bigint,
    fixedPrice?: bigint,
    _startingPrice?: bigint,
    options?: OptionsWriteMethod
  ): Promise<Output<void>> {
    this.validateListForSaleInputs(
      marketType,
      debtListing,
      fixedPrice,
      _startingPrice
    );

    options?.onServerSignPending?.();

    const { data, signature } = await UnlockdService.get(this.currency.chain.id).getMarketSignature(
      nft
    );

    const startingPrice =
      marketType === MarketItemType.TYPE_FIXED_PRICE
        ? fixedPrice!
        : _startingPrice!;

    const endAmount =
      marketType === MarketItemType.TYPE_AUCTION ||
        marketType === MarketItemType.TYPE_LIQUIDATION_AUCTION
        ? BigInt(0)
        : fixedPrice!;

    const startTime = BigInt(Math.floor(Date.now() / 1000));

    const endTime =
      marketType === MarketItemType.TYPE_FIXED_PRICE
        ? BigInt(Math.floor(new Date("12/31/2099").getTime() / 1000))
        : finishDate;

    const topicFilter = encodeEventTopics({
      abi: marketContract.abi,
      eventName: "MarketCreated",
    })[0];

    let correctedOptions: OptionsWriteMethod;

    if (options) {
      correctedOptions = {
        ...options,
        topicFilter,
      };
    } else {
      correctedOptions = { topicFilter };
    }

    const output = await marketContract.listForSale(
      this.underlyingAsset,
      marketType,
      startingPrice,
      endAmount,
      startTime,
      endTime,
      debtListing,
      data,
      signature,
      correctedOptions
    );

    if (!output.log) {
      throw new Error("log not found");
    }

    const topics = decodeEventLog({
      abi: marketContract.abi,
      data: output.log.data,
      topics: output.log.topics,
      eventName: "MarketCreated",
    });

    const _endTime = Number(`${endTime.toString()}000`);
    const orderId = topics.args.orderId;

    NftModel.save(nft.nftId, {
      marketItemData: new MarketItemData(orderId, _endTime, marketType, []),
    });

    return output;
  }

  async cancelListForSale(
    nft: INft,
    options?: OptionsWriteMethod
  ): Promise<Output<void>> {
    const marketItemData = await nft.marketItemData;

    if (!(await nft.isCancelListAvailable) || !marketItemData) {
      throw new Error("cancel list is not available for this nft");
    }

    const output = await marketContract.cancel(marketItemData.id, options);

    MarketItemModel.deleteById(`${this.currency.chain.id}-${marketItemData.id}`);

    NftModel.save(nft.nftId, { marketItemData: undefined });

    TheGraphService.get(nft.chainId).removeMarketItemFromNftPromise(nft.assetId);

    return output;
  }

  private validateListForSaleInputs(
    marketType: MarketItemType,
    debtListing: bigint,
    fixedPrice?: bigint,
    startingPrice?: bigint
  ): void {
    if (marketType === MarketItemType.TYPE_LIQUIDATION_AUCTION) {
      throw new Error(
        "Liquidation auctions only can be created by the backend"
      );
    } else if (
      (marketType === MarketItemType.TYPE_FIXED_PRICE ||
        marketType === MarketItemType.TYPE_FIXED_PRICE_AND_AUCTION) &&
      fixedPrice === undefined
    ) {
      throw new Error("Fixed price is required for for this type of item");
    } else if (
      (marketType === MarketItemType.TYPE_AUCTION ||
        marketType === MarketItemType.TYPE_FIXED_PRICE_AND_AUCTION) &&
      startingPrice === undefined
    ) {
      throw new Error("Starting price is required for for this type of item");
    }

    if (debtListing < BigInt(0) || debtListing > BigInt(10000)) {
      throw new Error("Debt listing must be between range of 0 to 10000");
    }
  }
}
